/**
 * TASK-2-3 — 클레임 처리 기본 노출값 9종 (고도몰 ID51 §1 "클레임 처리 기본 노출 설정").
 *
 * 클레임 처리화면이 시점별(주문취소/교환접수/환불완료) 복원·지급 **기본 선택값**을 표시한다.
 * 관리자 건별 변경 가능하나 **실제 적용(복원/지급 실행)은 TASK-5**. TASK-2 는 노출만.
 *
 * 정책 단일정본 = order-service OrderPolicySettings(V35). BO 는 클레임 전용 경량 GET
 * `/api/admin/v1/claims/processing-defaults`(claim-scoped DTO, 9 boolean)으로 읽는다.
 * 경로가 generated OpenAPI 스펙에 미반영이라 orderPolicy 와 동일하게 adminFetch 로 직접 호출.
 *
 * 근거: 분석 §1 / §6-1,4. BE 미배포/누락 시 §1 하드 기본값(DEFAULT_CLAIM_PROCESSING_DEFAULTS)로 폴백.
 */

/** 클레임 복원/지급 기본값 9종(전부 boolean). 카멜키 = order-service OrderPolicySettings 와 1:1. */
export interface ClaimProcessingDefaults {
  // 주문취소 시
  /** 재고 수량 복원 (기본: 복원함=true). */
  cancelRestoreStock: boolean;
  /** 쿠폰 복원 (기본: 복원안함=false). */
  cancelRestoreCoupon: boolean;
  /** 사은품 지급 (기본: 지급함=true). */
  cancelGrantFreebie: boolean;
  // 교환접수 시
  /** 교환취소 상품에 사용한 쿠폰 복원 (기본: 복원안함=false). */
  exchangeRestoreCoupon: boolean;
  /** 교환취소 상품의 사은품 지급 (기본: 지급함=true). */
  exchangeGrantFreebie: boolean;
  /** 교환추가 상품 마일리지 지급 (기본: 지급함=true). */
  exchangeGrantMileage: boolean;
  /** 교환추가 상품 쿠폰 마일리지 지급 (기본: 지급함=true). */
  exchangeGrantCouponMileage: boolean;
  // 환불완료 시
  /** 재고 수량 복원 (기본: 복원안함=false). */
  refundRestoreStock: boolean;
  /** 쿠폰 복원 (기본: 복원안함=false). */
  refundRestoreCoupon: boolean;
}

/**
 * 고도몰 §1 정책 기본값(BE 미배포/응답 누락 시 폴백). 분석 §1 표 그대로.
 */
export const DEFAULT_CLAIM_PROCESSING_DEFAULTS: ClaimProcessingDefaults = {
  cancelRestoreStock: true,
  cancelRestoreCoupon: false,
  cancelGrantFreebie: true,
  exchangeRestoreCoupon: false,
  exchangeGrantFreebie: true,
  exchangeGrantMileage: true,
  exchangeGrantCouponMileage: true,
  refundRestoreStock: false,
  refundRestoreCoupon: false,
};

/** 클레임 처리 기본값 표시 그룹(시점별). UI 가 시점 단위로 묶어 노출한다. */
export type ClaimDefaultScope = 'cancel' | 'exchange' | 'refund';

/** 시점별 표시 항목(키 = ClaimProcessingDefaults 필드, label = 한국어 노출명). */
export interface ClaimDefaultItem {
  key: keyof ClaimProcessingDefaults;
  label: string;
}

export const CLAIM_DEFAULT_GROUPS: ReadonlyArray<{
  scope: ClaimDefaultScope;
  title: string;
  items: ReadonlyArray<ClaimDefaultItem>;
}> = [
  {
    scope: 'cancel',
    title: '주문취소 시',
    items: [
      { key: 'cancelRestoreStock', label: '재고 수량 복원' },
      { key: 'cancelRestoreCoupon', label: '쿠폰 복원' },
      { key: 'cancelGrantFreebie', label: '사은품 지급' },
    ],
  },
  {
    scope: 'exchange',
    title: '교환접수 시',
    items: [
      { key: 'exchangeRestoreCoupon', label: '교환취소 상품 쿠폰 복원' },
      { key: 'exchangeGrantFreebie', label: '교환취소 상품 사은품 지급' },
      { key: 'exchangeGrantMileage', label: '교환추가 상품 마일리지 지급' },
      { key: 'exchangeGrantCouponMileage', label: '교환추가 상품 쿠폰 마일리지 지급' },
    ],
  },
  {
    scope: 'refund',
    title: '환불완료 시',
    items: [
      { key: 'refundRestoreStock', label: '재고 수량 복원' },
      { key: 'refundRestoreCoupon', label: '쿠폰 복원' },
    ],
  },
];
